import {Component, ComponentRef, OnDestroy, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {CwCaseObjectComponent} from '../cw-case-object-component/cw-case-object.component';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: Overlay
 * Reason: TM-1000002000 — in the consultant shell a Vorgang opened as an OBJECT OVERLAY (the case card
 *   of a ticket, the case inline card, any {@code addOverlay} caller) bypasses the routes. This component
 *   is the consultant's {@code CW_CASE_PAGE_COMPONENT} and therefore has to apply the SAME rule the
 *   route {@code cw/case/:id} applies — which, since 22.09.2026, is "open the Vorgang".
 */

/**
 * Consultant overlay of {@code cw.CwCase} — the Vorgang, in every phase.
 *
 * <p>Provided ONLY by the consultant shell ({@code CONSULTANT_PROVIDERS}); every other shell keeps the
 * CwModule default {@code CwCaseObjectComponent}. No shell detection lives here.
 *
 * <h3>[22.09.2026] The phase read is gone — and why that is the fix, not a loosening</h3>
 * This component used to call {@code readCwConsultantCaseView}: it read the case, showed a skeleton
 * until the answer arrived, and from CAPTURED on (or on any read error) rendered the reduced
 * Vorgangsansicht instead of the Vorgang. The route guard on {@code cw/case/:id} did the same thing
 * with a redirect. Both were removed together with the {@code /documents} branch of the row-click
 * resolver (Marko's decision, {@code cw-consultant-case-navigation.util.ts}): the restriction now sits
 * ON THE STEP and is enforced by the SERVER — {@code CwCaseStepViewService} hides a step the caller
 * may not work on, and the step guard refuses every step change before the first write. A restricted
 * step draws its configured Ersatzanzeige inside the case, and a {@code DOCUMENT_UPLOAD} Ersatzanzeige
 * embeds the reduced Vorgangsansicht itself. Redirecting the whole overlay to that view could only
 * have hidden the mechanism that does the actual gating.
 *
 * <p>Leaving the read in place HERE while the route no longer has it would be worse than either rule
 * on its own: the same Vorgang would open as the Vorgang from „Meine Anträge" and as the reduced view
 * from a ticket's case card. One rule, both entry points.
 *
 * <h3>Why the Vorgang is still created dynamically</h3>
 * {@code mvs-object-page} instantiates its page component and assigns {@code objectIdentifier} and
 * {@code isOverlay} as plain fields (no {@code setInput}, hence no {@code ngOnChanges}). This component
 * does exactly the same with {@code CwCaseObjectComponent}, so the Vorgang behaves in the overlay as it
 * does under {@code mvs-object-page} — that contract, not the phase, is this component's remaining job.
 *
 * @ticket TM-1000002000
 */
@Component({
    selector: 'mvs-cw-case-consultant-overlay',
    templateUrl: './cw-case-consultant-overlay.component.html',
    standalone: false
})
export class CwCaseConsultantOverlayComponent implements OnInit, OnDestroy {

    /** Assigned by {@code mvs-object-page} right after creation, before the first change detection. */
    objectIdentifier: ObjectIdentifier;

    /** Assigned by {@code mvs-object-page}; handed on to the Vorgang. */
    isOverlay: boolean = false;

    @ViewChild('fullCaseHost', {read: ViewContainerRef, static: true})
    fullCaseHost!: ViewContainerRef;

    private fullCaseRef: ComponentRef<CwCaseObjectComponent> | null = null;

    ngOnInit(): void {
        // No id, no case: mvs-object-page may create the component before it has one, and inventing
        // an objectIdentifier for CwCaseObjectComponent would make it read case `undefined`.
        if (this.objectIdentifier?.objectId === null || this.objectIdentifier?.objectId === undefined) {
            return;
        }
        this.createFullCase();
    }

    ngOnDestroy(): void {
        this.fullCaseHost?.clear();
        this.fullCaseRef = null;
    }

    private createFullCase(): void {
        if (this.fullCaseRef) {
            return;
        }
        this.fullCaseRef = this.fullCaseHost.createComponent(CwCaseObjectComponent);
        this.fullCaseRef.instance.objectIdentifier = this.objectIdentifier;
        (this.fullCaseRef.instance as unknown as {isOverlay: boolean}).isOverlay = this.isOverlay;
    }
}
